/**
 * RulesAtRiskView Component
 *
 * Tab-based view for displaying compliance rules in the audit sidebar.
 * Shows "At Risk" rules (non-compliant and warnings) and "Compliant" rules
 * in separate tabs with a cleaner card-based layout.
 *
 * Features staggered fade-in animations when rules are added to the list.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check as CheckmarkIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { AnimatePresence, motion } from 'framer-motion';

import type { RuleComplianceAssessment } from '../../types';

interface RulesAtRiskViewProps {
  rules: RuleComplianceAssessment[];
  onViewRule: (ruleId: string) => void;
  onFixWithAI: (reason: string) => void;
  onScrollToSection?: (sectionId: string) => void;
  onRuleClicked?: (ruleId: string, complianceStatus: string, hasRelevantSections: boolean) => void;
  onRuleFixClicked?: (ruleId: string, complianceStatus: string) => void;
  onRuleInfoViewed?: (ruleId: string) => void;
  onDismissedRulesChange?: (dismissedCount: number) => void;
  noteId?: string;
  encounterId?: string;
}

type TabValue = 'at-risk' | 'compliant';

/**
 * Individual rule card matching the new design
 */
const RuleCard: React.FC<{
  rule: RuleComplianceAssessment;
  onView: (ruleId: string) => void;
  onFix: (reason: string) => void;
  onScrollToSection?: (sectionId: string) => void;
  onRuleClicked?: (ruleId: string, complianceStatus: string, hasRelevantSections: boolean) => void;
  onRuleFixClicked?: (ruleId: string, complianceStatus: string) => void;
  onRuleInfoViewed?: (ruleId: string) => void;
  showLabel?: boolean;
  isDismissed?: boolean;
  onDismiss?: (ruleId: string) => void;
}> = ({
  rule,
  onView,
  onFix,
  onScrollToSection,
  onRuleClicked,
  onRuleFixClicked,
  onRuleInfoViewed,
  showLabel = true,
  isDismissed = false,
  onDismiss,
}) => {
  const theme = useTheme();
  const hasRelevantSections = rule.relevant_sections && rule.relevant_sections.length > 0;

  // Determine label and color based on compliance status
  const isActionNeeded = rule.compliant === 'no';
  const label = isActionNeeded ? 'ACTION NEEDED' : 'ACTION SUGGESTED';
  const labelColor = isActionNeeded ? theme.palette.error.main : theme.palette.warning.main;

  const handleCardClick = () => {
    onRuleClicked?.(rule.rule_id, rule.compliant, Boolean(hasRelevantSections));
    // Open rule detail modal
    onView(rule.rule_id);
  };

  const handleViewClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onRuleInfoViewed?.(rule.rule_id);
    // Scroll to relevant section in note
    if (hasRelevantSections && onScrollToSection) {
      const firstSectionId = rule.relevant_sections?.[0];
      if (firstSectionId) onScrollToSection(firstSectionId);
    }
  };

  const handleFixClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onRuleFixClicked?.(rule.rule_id, rule.compliant);
    // Format message: "We need to update the note for compliance with this rule: <rule name>. <suggestion>"
    const ruleName = rule.rule_title || rule.rule_id;
    const suggestion = rule.suggestion;
    const fixMessage = suggestion
      ? `We need to update the note for compliance with this rule: ${ruleName}. ${suggestion}`
      : `We need to update the note for compliance with this rule: ${ruleName}.`;
    onFix(fixMessage);
  };

  const handleDismissClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDismiss?.(rule.rule_id);
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 2,
        bgcolor: isDismissed ? theme.palette.grey[100] : 'background.paper',
        border: '1px solid',
        borderColor: isDismissed ? theme.palette.grey[300] : 'borderColors.lightprimary',
        cursor: 'pointer',
        opacity: isDismissed ? 0.5 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: isDismissed ? theme.palette.grey[400] : 'borderColors.brand',
          boxShadow: isDismissed ? 0 : 1,
        },
        '&:last-child': { mb: 0 },
      }}
    >
      {/* Row 1: Rule Title + Action Type Badge */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
          mb: rule.suggestion ? 0.75 : 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'textColors.darkText',
            fontWeight: 400,
            lineHeight: 1.4,
            flex: 1,
          }}
        >
          {rule.rule_title || rule.rule_id}
        </Typography>

        {/* Action Type Badge - only show for at-risk rules */}
        {showLabel && (
          <Typography
            variant="caption"
            sx={{
              color: labelColor,
              fontWeight: 600,
              fontSize: '0.65rem',
              letterSpacing: '0.3px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {label}
          </Typography>
        )}
      </Box>

      {/* Row 2: Suggestion + Action Icons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* Suggestion text */}
        <Typography
          variant="caption"
          sx={{
            color: 'textColors.lightText',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {rule.suggestion || ''}
        </Typography>

        {/* Action Icons */}
        <Box sx={{ display: 'flex', gap: 0, flexShrink: 0 }}>
          <Tooltip title={hasRelevantSections ? 'View in note' : 'No relevant sections'}>
            <span>
              <IconButton
                size="small"
                onClick={handleViewClick}
                disabled={!hasRelevantSections}
                sx={{
                  color: 'textColors.lightText',
                  p: 0.5,
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'backgroundColors.secondary',
                  },
                  '&:disabled': {
                    color: 'action.disabled',
                  },
                }}
              >
                <ViewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Fix with AI">
            <IconButton
              size="small"
              onClick={handleFixClick}
              sx={{
                color: 'textColors.lightText',
                p: 0.5,
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'backgroundColors.secondary',
                },
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={isDismissed ? 'Dismissed' : 'Mark as done'}>
            <IconButton
              size="small"
              onClick={handleDismissClick}
              disabled={isDismissed}
              sx={{
                color: isDismissed ? 'success.main' : 'textColors.lightText',
                p: 0.5,
                '&:hover': {
                  color: 'success.main',
                  bgcolor: 'backgroundColors.secondary',
                },
              }}
            >
              <CheckmarkIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// Animation variants for staggered fade-in effect
const ruleCardVariants = {
  hidden: {
    opacity: 0,
    y: -16,
    scale: 0.97,
  },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: index * 0.04, // Stagger delay: 30ms between each card
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for smooth deceleration
    },
  }),
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: {
      duration: 0.15,
    },
  },
};

/**
 * RulesAtRiskView - Tab-based view for compliance rules
 */
export const RulesAtRiskView: React.FC<RulesAtRiskViewProps> = ({
  rules,
  onViewRule,
  onFixWithAI,
  onScrollToSection,
  onRuleClicked,
  onRuleFixClicked,
  onRuleInfoViewed,
  onDismissedRulesChange,
  noteId,
  encounterId,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>('at-risk');
  const [dismissedRuleIds, setDismissedRuleIds] = useState<Set<string>>(new Set());
  const { noteAudit } = useEventTracking();

  // Notify parent when dismissed rules change
  useEffect(() => {
    onDismissedRulesChange?.(dismissedRuleIds.size);
  }, [dismissedRuleIds, onDismissedRulesChange]);

  // Track which rules have been seen (for animation purposes)
  // Uses ref to persist across renders without causing re-renders
  const seenRuleIdsRef = useRef<Set<string>>(new Set());

  // Group rules
  const atRiskRules = rules.filter(r => r.compliant === 'no' || r.compliant === 'warning');
  const compliantRules = rules.filter(r => r.compliant === 'yes');

  // Calculate which rules are new THIS render (synchronously, before render completes)
  // This ensures new rules start with initial="hidden" on their first render
  const newRuleIdsThisRender = useMemo(() => {
    const newIds: string[] = [];
    rules.forEach(rule => {
      if (!seenRuleIdsRef.current.has(rule.rule_id)) {
        newIds.push(rule.rule_id);
      }
    });
    return newIds;
  }, [rules]);

  // After render, mark new rules as seen (so they don't animate again)
  useEffect(() => {
    if (newRuleIdsThisRender.length > 0) {
      newRuleIdsThisRender.forEach(id => seenRuleIdsRef.current.add(id));
    }
  }, [newRuleIdsThisRender]);

  // Sort at-risk rules: Action Needed first, then Action Suggested, then dismissed at bottom
  const sortedAtRiskRules = useMemo(() => {
    return [...atRiskRules].sort((a, b) => {
      const aIsDismissed = dismissedRuleIds.has(a.rule_id);
      const bIsDismissed = dismissedRuleIds.has(b.rule_id);

      // Dismissed rules go to bottom
      if (aIsDismissed !== bIsDismissed) return aIsDismissed ? 1 : -1;

      // Action Needed ('no') before Action Suggested ('warning')
      if (a.compliant !== b.compliant) {
        return a.compliant === 'no' ? -1 : 1;
      }

      return 0;
    });
  }, [atRiskRules, dismissedRuleIds]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    // Track tab switching
    noteAudit.tabSwitched({
      note_id: noteId,
      encounter_id: encounterId,
      previous_tab: activeTab,
      new_tab: newValue,
      at_risk_count: atRiskRules.length,
      compliant_count: compliantRules.length,
    });
    setActiveTab(newValue);
  };

  const handleDismissRule = (ruleId: string) => {
    const rule = rules.find(r => r.rule_id === ruleId);
    const newDismissedSet = new Set(dismissedRuleIds).add(ruleId);
    setDismissedRuleIds(newDismissedSet);

    // Track rule dismissed
    if (rule) {
      noteAudit.ruleDismissed({
        note_id: noteId,
        encounter_id: encounterId,
        rule_id: ruleId,
        compliance_status: rule.compliant === 'no' ? 'no' : 'warning',
        dismissed_count: newDismissedSet.size,
      });
    }
  };

  const currentRules = activeTab === 'at-risk' ? sortedAtRiskRules : compliantRules;

  // Calculate animation index for new rules (preserves stagger order based on position in current list)
  const getAnimationIndex = (ruleId: string): number => {
    const indexInNewRules = newRuleIdsThisRender.indexOf(ruleId);
    if (indexInNewRules === -1) return -1; // Not a new rule, no animation
    return indexInNewRules;
  };

  // Check if a rule should animate (is new this render)
  const shouldAnimateRule = (ruleId: string): boolean => {
    return newRuleIdsThisRender.includes(ruleId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'borderColors.lightprimary' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
            },
          }}
        >
          <Tab label="At Risk" value="at-risk" />
          <Tab label="Compliant" value="compliant" />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        {/* Section Header */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {activeTab === 'at-risk' ? 'Rules at Risk' : 'Compliant Rules'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'textColors.lightText', mb: 2, lineHeight: 1.4 }}>
          {activeTab === 'at-risk'
            ? "These rules aren't yet satisfied in your note based on payer requirements. Select a rule to understand what's missing and update your note to prevent claim rejection."
            : 'These rules are satisfied in your note.'}
        </Typography>

        {/* Rules List with staggered fade-in animation */}
        {currentRules.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <AnimatePresence mode="popLayout">
              {currentRules.map(rule => {
                const isNewRule = shouldAnimateRule(rule.rule_id);
                const animationIndex = getAnimationIndex(rule.rule_id);

                return (
                  <motion.div
                    key={rule.rule_id}
                    variants={ruleCardVariants}
                    initial={isNewRule ? 'hidden' : false}
                    animate="visible"
                    exit="exit"
                    custom={isNewRule ? animationIndex : 0}
                    layout
                    layoutId={rule.rule_id}
                  >
                    <RuleCard
                      rule={rule}
                      onView={onViewRule}
                      onFix={onFixWithAI}
                      onScrollToSection={onScrollToSection}
                      onRuleClicked={onRuleClicked}
                      onRuleFixClicked={onRuleFixClicked}
                      onRuleInfoViewed={onRuleInfoViewed}
                      showLabel={activeTab === 'at-risk'}
                      isDismissed={dismissedRuleIds.has(rule.rule_id)}
                      onDismiss={handleDismissRule}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {activeTab === 'at-risk' ? (
              <>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                  All rules compliant
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This note meets all applicable rules
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No compliant rules to display
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RulesAtRiskView;
